import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTenant } from '../contexts/TenantContext';
import { getDefaultRedirectPath } from '../utils/redirect';
import { useTranslation } from 'react-i18next';

/**
 * NoAccess: shown when route does not match or user has no access.
 * Safe mechanism: if allowed pages exist, redirect to default page.
 */
export function NoAccess() {
  const navigate = useNavigate();
  const { tenants, tenantPages, tenantBots, loading } = useTenant();
  const { t } = useTranslation();

  const totalPages = Object.values(tenantPages || {}).reduce((sum, pages) => sum + pages.length, 0);
  const totalBots = Object.values(tenantBots || {}).reduce((sum, bots) => sum + bots.length, 0);
  const hasAccess = (tenants?.length ?? 0) > 0 && (totalPages > 0 || totalBots > 0);

  useEffect(() => {
    if (loading) return;
    if (hasAccess) {
      const path = getDefaultRedirectPath(tenants || [], tenantPages || {}, tenantBots || {});
      navigate(path, { replace: true });
    }
  }, [loading, hasAccess, tenants, tenantPages, tenantBots, navigate]);

  return (
    <div className="flex items-center justify-center h-full w-full bg-surface-2">
      <div className="text-center px-6 max-w-md">
        <div className="mb-6">
          <svg className="w-20 h-20 mx-auto text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-semibold text-ink mb-2">
          {t('noAccess.title')}
        </h1>
        <p className="text-muted mb-1">
          {loading ? t('noAccess.checking') : t('noAccess.none')}
        </p>
        <p className="text-sm text-muted">
          {loading ? t('noAccess.wait') : t('noAccess.askAdmin')}
        </p>
      </div>
    </div>
  );
}

